// staff-data.js - pure helpers for staff roles. No DOM, no network.
//
// The club splits staff three ways: people who coach a team, people who run
// training sessions, and people who do both. That is stored as two booleans
// (is_coach, is_trainer) rather than one enum, because someone can gain or lose
// either half independently and `is_trainer` already carries a second meaning:
// it gates the trainer-only section of the DTA Library.

export const STAFF_TYPES = [
  { key: 'coach',   labelKey: 'staff.typeCoach' },
  { key: 'trainer', labelKey: 'staff.typeTrainer' },
  { key: 'both',    labelKey: 'staff.typeBoth' },
  { key: 'none',    labelKey: 'staff.typeNone' },
];

/**
 * Which of the four buckets a staff member falls in.
 * 'none' means the role has not been set yet (e.g. just approved), which is
 * worth surfacing rather than hiding: those people can log in but have no job.
 */
export function staffType(s) {
  const coach   = !!s?.is_coach;
  const trainer = !!s?.is_trainer;
  if (coach && trainer) return 'both';
  if (coach)            return 'coach';
  if (trainer)          return 'trainer';
  return 'none';
}

/**
 * Filter a roster by type. A null/empty type means "everyone".
 * Asking for 'coach' or 'trainer' INCLUDES coach-trainers, because someone who
 * does both is still a coach when you are looking for coaches. Ask for 'both'
 * to get only the people doing double duty.
 */
export function filterStaffByType(list, type) {
  if (!type) return [...(list || [])];
  return (list || []).filter(s => {
    const t = staffType(s);
    if (type === 'coach')   return t === 'coach'   || t === 'both';
    if (type === 'trainer') return t === 'trainer' || t === 'both';
    return t === type;
  });
}

/** Counts per bucket, for the filter labels. Coach/trainer include 'both'. */
export function staffTypeCounts(list) {
  const all = list || [];
  return {
    all:     all.length,
    coach:   filterStaffByType(all, 'coach').length,
    trainer: filterStaffByType(all, 'trainer').length,
    both:    filterStaffByType(all, 'both').length,
    none:    filterStaffByType(all, 'none').length,
  };
}
